/**
 * EmployeesTab — Lista de empleados con CRUD completo, acciones y KebabMenu.
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Search, UserCheck, UserX, Clock, Pencil, Eye, MoreVertical, UserMinus, Mail, Loader2, Upload } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import {
  useEmployees, useCreateEmployee, useUpdateEmployee, usePatchEmployee, useBulkInvitePortal,
  useEmployeeImportFields, useImportEmployees, usePreviewEmployeeImport,
  type Employee,
} from '../hooks/usePayroll'
import type { PrevisionalPayload } from './EmployeeModal'
import { usePermission } from '../../../hooks/usePermission'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { ConfirmModal, useConfirmModal } from '../../../components/ConfirmModal'
import { extractErrorMessage, extractFieldErrors } from '../../../lib/errorUtils'
import { ImportCSVMapperModal, type SystemField } from '@/components/ImportCSVMapperModal'
import EmployeeModal from './EmployeeModal'
import EmployeeDetailModal from './EmployeeDetailModal'
import { Pagination } from '@/components/ds/Pagination'

const STATUS_BADGE: Record<string, { label: string; cls: string; icon: typeof UserCheck }> = {
  active:     { label: 'Activo', cls: 'bg-success-surface-strong text-success-content', icon: UserCheck },
  on_leave:   { label: 'Con Licencia', cls: 'bg-warning-surface-strong text-warning-content', icon: Clock },
  terminated: { label: 'Desvinculado', cls: 'bg-danger-surface-strong text-danger-content', icon: UserX },
}

export default function EmployeesTab() {
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [editing, setEditing] = useState<Partial<Employee> | null>(null)
  // `?ficha=<id>` abre la ficha al entrar: así otras pantallas (el detalle de
  // un envío de documentos) llevan directo a la persona que hay que corregir.
  const [params, setParams] = useSearchParams()
  const [detailId, setDetailId] = useState<string>(params.get('ficha') ?? '')
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()
  const qc = useQueryClient()

  const { data, isLoading } = useEmployees({ ordering: sortStateToParam(orden), search: search || undefined, status: statusFilter || undefined, page, page_size: 20 })
  const { mutate: create, isPending: isCreating } = useCreateEmployee()
  const { mutate: update, isPending: isUpdating } = useUpdateEmployee(editing?.id || '')
  const { mutateAsync: patchEmployee } = usePatchEmployee()
  const { confirm, modalProps } = useConfirmModal()
  const { mutate: bulkInvite, isPending: isBulkInviting } = useBulkInvitePortal()

  // Importación masiva (#21). Los campos los sirve el backend: es la fuente única.
  const { data: importFields } = useEmployeeImportFields()
  const importMutation = useImportEmployees()
  const previewMutation = usePreviewEmployeeImport()

  const importSystemFields: SystemField[] = (importFields || []).map(f => ({
    key: f.key,
    label: f.group ? `${f.group} · ${f.label}` : f.label,
    required: f.required,
    // El auto-match compara contra la clave, la etiqueta y los alias; se manda
    // también el label pelado porque el de arriba lleva el grupo como prefijo.
    aliases: [...f.aliases, f.label],
  }))

  const handleBulkInvite = () => {
    confirm({
      message: '¿Enviar la invitación al portal a todos los empleados activos elegibles (con email y sin cuenta ERP)?',
      variant: 'success',
      confirmLabel: 'Enviar invitaciones',
      onConfirm: () => bulkInvite(undefined, {
        onSuccess: (r) => {
          const parts = [`${r.invited} invitaciones enviadas`]
          if (r.already_activated) parts.push(`${r.already_activated} ya activadas`)
          if (r.no_email) parts.push(`${r.no_email} sin email`)
          if (r.erp_linked) parts.push(`${r.erp_linked} con cuenta ERP`)
          if (r.terminated) parts.push(`${r.terminated} desvinculados`)
          if (r.email_in_use) {
            parts.push(`${r.email_in_use} con un correo que ya usa otra ficha (corregir el correo e invitar desde la ficha)`)
            toast.warning(parts.join(' · '))
            return
          }
          toast.success(parts.join(' · '))
        },
        onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al invitar al portal')),
      }),
    })
  }

  const employees = data?.employees || []
  const total = data?.count || 0
  const activeCount: number | undefined = data?.active_count
  const employeeLimit: number | null | undefined = data?.employee_limit
  const limitReached = employeeLimit != null && activeCount != null && activeCount >= employeeLimit

  const handleSubmit = (formData: Partial<Employee>, previsional?: PrevisionalPayload | null) => {
    // Limpiar campos FK vacíos para evitar errores de validación
    const cleaned = { ...formData }
    if (!(cleaned as any).cost_center) delete (cleaned as any).cost_center
    if (!(cleaned as any).user) delete (cleaned as any).user
    if (!(cleaned as any).birth_date) (cleaned as any).birth_date = null
    if (!(cleaned as any).termination_date) (cleaned as any).termination_date = null
    delete (cleaned as any).full_name  // read-only
    const mutation = editing?.id ? update : create
    mutation(cleaned, {
      // Guarda la configuración previsional en el mismo paso que el empleado.
      // Se orquesta acá (el padre queda montado tras cerrar el modal, así no se
      // pierde el guardado). Si falla la previsión, el empleado YA quedó guardado:
      // se avisa y se deja el modal abierto para reintentar.
      onSuccess: async () => {
        if (previsional) {
          try {
            if (previsional.id) {
              await apiClient.patch(`/payroll/previsional/${previsional.id}/`, previsional.data)
            } else {
              await apiClient.post('/payroll/previsional/', previsional.data)
            }
            qc.invalidateQueries({ queryKey: ['previsional'] })
          } catch (e: any) {
            toast.error(extractErrorMessage(e, 'Empleado guardado, pero no se pudo guardar la configuración previsional.'))
            return
          }
        }
        toast.success(editing?.id ? 'Empleado actualizado' : 'Empleado creado')
        setModalOpen(false)
        setEditing(null)
      },
      onError: (e: any) => {
        // Mostrar el detalle por campo (ej. "RUT: Ya existe un empleado con este
        // RUT") en vez del genérico "Los datos enviados no son válidos", que no
        // decía qué corregir (incidente Jacqueline 2026-06-XX).
        const fieldErrors = extractFieldErrors(e)
        if (fieldErrors) {
          const LABELS: Record<string, string> = {
            rut: 'RUT', first_name: 'Nombres', last_name: 'Apellidos',
            email: 'Email', phone: 'Teléfono', hire_date: 'Fecha de contratación',
            bank_account_number: 'N° de cuenta', bank_name: 'Banco',
            gender: 'Género', marital_status: 'Estado civil',
          }
          const detail = Object.entries(fieldErrors)
            .map(([f, m]) => `${LABELS[f] ?? f}: ${m}`).join(' · ')
          toast.error(detail)
        } else {
          toast.error(extractErrorMessage(e, 'Error al guardar empleado'))
        }
      },
    })
  }

  // Cambio rápido de estado: el finiquito se hace aparte, en la pestaña Finiquitos.
  // La mutación recibe el id al ejecutarse: antes se creaba un hook dentro del clic
  // («Invalid hook call») y el PATCH iba al empleado en edición, no al de la fila (#1458).
  const handleTerminate = (emp: Employee) => {
    confirm({
      message: `¿Desvincular a ${emp.full_name || emp.first_name + ' ' + emp.last_name}? `
        + 'Queda con fecha de término hoy; el finiquito se crea en la pestaña Finiquitos.',
      confirmLabel: 'Desvincular',
      onConfirm: async () => {
        try {
          await patchEmployee({ id: emp.id, status: 'terminated', termination_date: todayISO() } as Partial<Employee> & { id: string })
          toast.success('Empleado marcado como desvinculado. El finiquito se crea en la pestaña Finiquitos.')
        } catch (e) {
          toast.error(extractErrorMessage(e, 'No se pudo desvincular al empleado'))
        }
      },
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar nombre o RUT" type="text" placeholder="Buscar nombre o RUT..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <select aria-label="Estado" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
          <option value="">Todos</option>
          <option value="active">Activos</option>
          <option value="on_leave">Con Licencia</option>
          <option value="terminated">Desvinculados</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          {can('payroll.employees', 'edit') && (
            <button
              onClick={handleBulkInvite}
              disabled={isBulkInviting}
              title="Enviar invitación al portal a todos los empleados activos sin cuenta"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-primary border border-primary-100 rounded-lg hover:bg-primary-50 disabled:opacity-50">
              {isBulkInviting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Invitar al portal
            </button>
          )}
          {can('payroll.employees', 'create') && (
            <button
              onClick={() => setImportOpen(true)}
              title="Cargar varios trabajadores desde un CSV o Excel"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-content border border-divider rounded-lg hover:bg-soft transition-colors">
              <Upload size={14} /> Importar
            </button>
          )}
          {can('payroll.employees', 'create') && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true) }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
              <Plus size={14} /> Nuevo empleado
            </button>
          )}
        </div>
      </div>

      {employeeLimit != null && activeCount != null && (
        <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className={`font-medium ${limitReached ? 'text-warning-content-soft' : 'text-content-muted'}`}>
            {activeCount} de {employeeLimit} trabajadores activos
          </span>
          {limitReached && (
            <span className="text-xs text-warning-content-soft">
              · Llegaste al tope de tu plan. Contacta a soporte para ampliarlo.
            </span>
          )}
        </div>
      )}

      <DataTable<Employee>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          { key: 'full_name', header: 'Nombre', sortable: true,
            sortValue: e => e.full_name || `${e.first_name} ${e.last_name}`,
            render: e => (
              <span className="inline-flex items-center gap-2 font-medium text-content">
                {e.full_name || `${e.first_name} ${e.last_name}`}
                {e.contract_expiry?.expiring && (
                  <span
                    title={`Contrato a plazo fijo vence el ${e.contract_expiry.end_date} (${e.contract_expiry.days_left} días)`}
                    className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-warning-surface-strong text-warning-content">
                    Contrato por vencer
                  </span>
                )}
              </span>
            ) },
          { key: 'rut', header: 'RUT', sortable: true,
            render: e => <span className="text-content-muted font-mono">{e.rut}</span> },
          { key: 'current_position', header: 'Cargo',
            render: e => <span className="text-content-muted">{e.current_position || '—'}</span> },
          { key: 'hire_date', header: 'Ingreso', sortable: true,
            render: e => <span className="text-content-muted">{formatDate(e.hire_date, locale)}</span> },
          { key: 'status', header: 'Estado', sortable: true,
            sortValue: e => (STATUS_BADGE[e.status] || STATUS_BADGE.active).label,
            render: e => {
              const badge = STATUS_BADGE[e.status] || STATUS_BADGE.active
              const Icon = badge.icon
              return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${badge.cls}`}>
                  <Icon size={12} /> {badge.label}
                </span>
              )
            } },
          { key: 'acciones', header: 'Acciones', width: 96,
            render: e => (
              <div className="flex items-center justify-center gap-1" onClick={ev => ev.stopPropagation()}>
                {can('payroll.employees', 'edit') && (
                  <button onClick={() => { setEditing(e); setModalOpen(true) }} title="Editar"
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                    <Pencil size={14} />
                  </button>
                )}
                {can('payroll.employees', 'edit') && e.status === 'active' && (
                  <button onClick={() => handleTerminate(e)} title="Desvincular"
                    className="p-1.5 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-solid">
                    <UserMinus size={14} />
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={employees}
        rowKey={e => e.id}
        minWidth={820}
        loading={isLoading}
        onRowClick={e => setDetailId(e.id)}
        empty={<EmptyState title="Sin empleados registrados." size="sm" />}
        caption="Trabajadores de la empresa"
      />

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      <EmployeeModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null) }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        initial={editing}
      />

      <EmployeeDetailModal
        employeeId={detailId}
        onClose={() => { setDetailId(''); if (params.has('ficha')) setParams({}, { replace: true }) }}
        onEdit={(emp) => { setDetailId(''); setEditing(emp); setModalOpen(true) }}
      />

      <ImportCSVMapperModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false)
          // Al cerrar tras un import real la lista ya se invalidó en el hook.
        }}
        title="Importar trabajadores"
        description={
          'Cada fila crea al trabajador con su contrato y su configuración previsional, ' +
          'o actualiza al que ya exista con el mismo RUT. Antes de escribir nada se ' +
          'muestra un resumen de lo que va a pasar.'
        }
        systemFields={importSystemFields}
        templateFileName="plantilla_trabajadores.csv"
        accept={['.csv', '.xlsx']}
        onPreview={(file) => previewMutation.mutateAsync(file)}
        onDryRun={(file, mapping) => importMutation.mutateAsync({ file, mapping, dryRun: true })}
        onImport={async (file, mapping) => {
          const res = await importMutation.mutateAsync({ file, mapping, dryRun: false })
          if (res.created || res.updated) {
            toast.success(
              `${res.created} trabajadores creados y ${res.updated} actualizados.`,
            )
          }
          return res
        }}
        isPending={importMutation.isPending || previewMutation.isPending}
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
      <ConfirmModal {...modalProps} />
    </>
  )
}
